import type {
    Tenant,
    TenantSubscription,
    WorkspaceBillingInterval,
    WorkspacePlanId,
    WorkspaceAccessMode,
    WorkspaceSubscriptionStatus,
} from '~/types/models';
import { toMillis } from './timestamps';
import { getAiMonthlyRequestLimit } from './aiPolicy';

export type WorkspacePlanDefinition = {
    id: WorkspacePlanId,
    name: string,
    priceMonthly: number,
    includedUsers: number,
    storageGb: number,
    aiActionsPerMonth: number,
    summary: string,
    bestFor: string,
    highlighted?: boolean,
    features: {
        crm: boolean,
        messaging: boolean,
        forms: boolean,
        clientPortal: 'basic' | 'branded' | 'fully_branded',
        projectLimit: number | 'unlimited',
        recurringTasks: boolean,
        workflows: boolean,
        projectTemplates: boolean,
        approvals: boolean,
        analytics: boolean,
        quotesInvoices: boolean,
        depositsAndPaymentPlans: boolean,
        customDomain: boolean,
        customEmailDomain: boolean,
        fullPlatformBranding: boolean,
        prioritySupport: boolean,
    },
};

export const EXTRA_USER_PRICE_GBP = 7;
export const EXTRA_STORAGE_BLOCK_GB = 50;
export const EXTRA_STORAGE_PRICE_GBP = 10;
export const GROWTH_BRANDING_ADDON_PRICE_GBP = 19;
export const DEFAULT_TRIAL_DAYS = 14;

export const WORKSPACE_PLAN_DEFINITIONS: Record<WorkspacePlanId, WorkspacePlanDefinition> = {
    starter: {
        id: 'starter',
        name: 'Starter',
        priceMonthly: 19,
        includedUsers: 2,
        storageGb: 5,
        aiActionsPerMonth: getAiMonthlyRequestLimit('starter'),
        summary: 'A strong foundation for freelancers and smaller teams.',
        bestFor: 'Freelancers and small teams getting organised.',
        features: {
            crm: true,
            messaging: true,
            forms: true,
            clientPortal: 'basic',
            projectLimit: 10,
            recurringTasks: false,
            workflows: false,
            projectTemplates: false,
            approvals: false,
            analytics: false,
            quotesInvoices: true,
            depositsAndPaymentPlans: false,
            customDomain: false,
            customEmailDomain: false,
            fullPlatformBranding: false,
            prioritySupport: false,
        },
    },
    growth: {
        id: 'growth',
        name: 'Growth',
        priceMonthly: 59,
        includedUsers: 5,
        storageGb: 50,
        aiActionsPerMonth: getAiMonthlyRequestLimit('growth'),
        summary: 'The core operating system plan for growing agencies.',
        bestFor: 'Growing agencies and small teams scaling delivery.',
        highlighted: true,
        features: {
            crm: true,
            messaging: true,
            forms: true,
            clientPortal: 'branded',
            projectLimit: 'unlimited',
            recurringTasks: true,
            workflows: true,
            projectTemplates: true,
            approvals: true,
            analytics: true,
            quotesInvoices: true,
            depositsAndPaymentPlans: true,
            customDomain: false,
            customEmailDomain: false,
            fullPlatformBranding: false,
            prioritySupport: false,
        },
    },
    pro: {
        id: 'pro',
        name: 'Pro',
        priceMonthly: 99,
        includedUsers: 10,
        storageGb: 150,
        aiActionsPerMonth: getAiMonthlyRequestLimit('pro'),
        summary: 'Full white-label control for established and scaling studios.',
        bestFor: 'Established agencies that want a fully branded client experience.',
        features: {
            crm: true,
            messaging: true,
            forms: true,
            clientPortal: 'fully_branded',
            projectLimit: 'unlimited',
            recurringTasks: true,
            workflows: true,
            projectTemplates: true,
            approvals: true,
            analytics: true,
            quotesInvoices: true,
            depositsAndPaymentPlans: true,
            customDomain: true,
            customEmailDomain: true,
            fullPlatformBranding: true,
            prioritySupport: true,
        },
    },
};

const WORKSPACE_PLAN_RANK: Record<WorkspacePlanId, number> = {
    starter: 0,
    growth: 1,
    pro: 2,
};

export type WorkspaceEntitlements = {
    plan: WorkspacePlanDefinition,
    billingPlan: WorkspacePlanDefinition,
    status: WorkspaceSubscriptionStatus,
    billingInterval: WorkspaceBillingInterval,
    trialEndsAt?: unknown,
    isFullFeatureTrial: boolean,
    isTrialExpired: boolean,
    seatsIncluded: number,
    additionalSeats: number,
    totalSeats: number,
    storageGb: number,
    paidAdditionalSeats: number,
    paidExtraStorageBlocks: number,
    operatorAdditionalSeats: number,
    operatorExtraStorageBlocks: number,
    operatorPlanSeatUplift: number,
    operatorPlanStorageUpliftGb: number,
    growthBrandingAddonEnabled: boolean,
    customDomainEnabled: boolean,
    customEmailDomainEnabled: boolean,
    fullPlatformBrandingEnabled: boolean,
    prioritySupportEnabled: boolean,
    accessMode: WorkspaceAccessMode,
    operatorOverrideActive: boolean,
    operatorOverridePlan?: WorkspacePlanId,
    operatorOverrideExpiresAt?: unknown,
    operatorOverrideNeverExpires: boolean,
};

function normalizePlanId(value?: string | null): WorkspacePlanId {
    if (value === 'growth' || value === 'pro' || value === 'starter') {
        return value;
    }
    return 'starter';
}

export function getTenantSubscription(tenant?: Tenant | null): TenantSubscription {
    const legacyPlan = normalizePlanId(tenant?.tier);
    const plan = normalizePlanId(tenant?.subscription?.plan || legacyPlan);
    const definition = WORKSPACE_PLAN_DEFINITIONS[plan];
    const additionalSeatsRaw = Number(tenant?.subscription?.additionalSeats ?? 0);
    const extraStorageBlocksRaw = Number(tenant?.subscription?.addOns?.extraStorageBlocks ?? 0);

    return {
        plan,
        status: tenant?.subscription?.status || 'trialing',
        billingInterval: tenant?.subscription?.billingInterval || 'monthly',
        trialEndsAt: tenant?.subscription?.trialEndsAt,
        startedAt: tenant?.subscription?.startedAt,
        currentPeriodEnd: tenant?.subscription?.currentPeriodEnd,
        cancelAtPeriodEnd: Boolean(tenant?.subscription?.cancelAtPeriodEnd),
        seatsIncluded: Number(tenant?.subscription?.seatsIncluded ?? definition.includedUsers),
        additionalSeats: Number.isFinite(additionalSeatsRaw) && additionalSeatsRaw > 0 ? Math.floor(additionalSeatsRaw) : 0,
        addOns: {
            growthBranding: Boolean(tenant?.subscription?.addOns?.growthBranding),
            extraStorageBlocks: Number.isFinite(extraStorageBlocksRaw) && extraStorageBlocksRaw > 0 ? Math.floor(extraStorageBlocksRaw) : 0,
        },
        stripeCustomerId: tenant?.subscription?.stripeCustomerId,
        stripeSubscriptionId: tenant?.subscription?.stripeSubscriptionId,
        operatorOverride: tenant?.subscription?.operatorOverride,
        updatedAt: tenant?.subscription?.updatedAt,
    };
}

export function resolveWorkspaceEntitlements(tenant?: Tenant | null): WorkspaceEntitlements {
    const subscription = getTenantSubscription(tenant);
    const billingPlan = WORKSPACE_PLAN_DEFINITIONS[subscription.plan];
    const trialEndsAtMs = toMillis(subscription.trialEndsAt);
    const isTrialExpired = subscription.status === 'trialing' && trialEndsAtMs > 0 && trialEndsAtMs < Date.now();
    const isFullFeatureTrial = subscription.status === 'trialing' && !isTrialExpired;
    const plan = isFullFeatureTrial
        ? WORKSPACE_PLAN_DEFINITIONS.pro
        : billingPlan;
    const growthBrandingAddonEnabled = billingPlan.id === 'growth' && Boolean(subscription.addOns?.growthBranding);
    const extraStorageBlocks = Number(subscription.addOns?.extraStorageBlocks || 0);
    const seatsIncluded = isFullFeatureTrial
        ? plan.includedUsers
        : Number(subscription.seatsIncluded || billingPlan.includedUsers);
    const additionalSeats = Number(subscription.additionalSeats || 0);
    const overrideStartsAtMs = toMillis(subscription.operatorOverride?.startsAt);
    const overrideExpiresAtMs = toMillis(subscription.operatorOverride?.expiresAt);
    const operatorOverrideActive = Boolean(
        subscription.operatorOverride
        && (subscription.operatorOverride.neverExpires === true || overrideExpiresAtMs > Date.now())
        && (!overrideStartsAtMs || overrideStartsAtMs <= Date.now()),
    );
    const operatorOverridePlanValue = String(subscription.operatorOverride?.plan || '');
    const operatorOverridePlan = operatorOverrideActive && ['starter', 'growth', 'pro'].includes(operatorOverridePlanValue)
        ? normalizePlanId(operatorOverridePlanValue)
        : null;
    const operatorAdditionalSeats = operatorOverrideActive
        ? Math.max(0, Math.floor(Number(subscription.operatorOverride?.additionalSeats || 0)))
        : 0;
    const operatorExtraStorageBlocks = operatorOverrideActive
        ? Math.max(0, Math.floor(Number(subscription.operatorOverride?.extraStorageBlocks || 0)))
        : 0;
    const accessMode: WorkspaceAccessMode = operatorOverrideActive
        && ['read_only', 'blocked'].includes(String(subscription.operatorOverride?.accessMode || ''))
        ? subscription.operatorOverride?.accessMode as WorkspaceAccessMode
        : 'normal';

    const operatorPlan = operatorOverridePlan
        ? WORKSPACE_PLAN_DEFINITIONS[operatorOverridePlan]
        : null;
    const entitlementPlan = operatorPlan
        && WORKSPACE_PLAN_RANK[operatorPlan.id] > WORKSPACE_PLAN_RANK[plan.id]
        ? operatorPlan
        : plan;
    const entitlementSeatsIncluded = Math.max(seatsIncluded, entitlementPlan.includedUsers);
    const operatorPlanSeatUplift = Math.max(0, entitlementSeatsIncluded - seatsIncluded);
    const operatorPlanStorageUpliftGb = Math.max(0, entitlementPlan.storageGb - plan.storageGb);

    return {
        plan: entitlementPlan,
        billingPlan,
        status: subscription.status || 'trialing',
        billingInterval: subscription.billingInterval || 'monthly',
        trialEndsAt: subscription.trialEndsAt,
        isFullFeatureTrial,
        isTrialExpired,
        seatsIncluded: entitlementSeatsIncluded,
        additionalSeats: additionalSeats + operatorAdditionalSeats,
        totalSeats: entitlementSeatsIncluded + additionalSeats + operatorAdditionalSeats,
        storageGb: entitlementPlan.storageGb + ((extraStorageBlocks + operatorExtraStorageBlocks) * EXTRA_STORAGE_BLOCK_GB),
        paidAdditionalSeats: additionalSeats,
        paidExtraStorageBlocks: extraStorageBlocks,
        operatorAdditionalSeats,
        operatorExtraStorageBlocks,
        operatorPlanSeatUplift,
        operatorPlanStorageUpliftGb,
        growthBrandingAddonEnabled,
        customDomainEnabled: entitlementPlan.features.customDomain || growthBrandingAddonEnabled,
        customEmailDomainEnabled: entitlementPlan.features.customEmailDomain,
        fullPlatformBrandingEnabled: entitlementPlan.features.fullPlatformBranding,
        prioritySupportEnabled: entitlementPlan.features.prioritySupport,
        accessMode,
        operatorOverrideActive,
        operatorOverridePlan: operatorOverridePlan || undefined,
        operatorOverrideExpiresAt: operatorOverrideActive && !subscription.operatorOverride?.neverExpires ? subscription.operatorOverride?.expiresAt : undefined,
        operatorOverrideNeverExpires: operatorOverrideActive && subscription.operatorOverride?.neverExpires === true,
    };
}

export function createDefaultWorkspaceSubscription(now = new Date()): TenantSubscription {
    const trialEndsAt = new Date(now);
    trialEndsAt.setDate(trialEndsAt.getDate() + DEFAULT_TRIAL_DAYS);

    return {
        plan: 'starter',
        status: 'trialing',
        billingInterval: 'monthly',
        trialEndsAt,
        startedAt: now,
        seatsIncluded: WORKSPACE_PLAN_DEFINITIONS.starter.includedUsers,
        additionalSeats: 0,
        addOns: {
            growthBranding: false,
            extraStorageBlocks: 0,
        },
    };
}

export function formatPlanPrice(price: number): string {
    return `£${price}/mo`;
}
